import { useState, useEffect, useRef, useCallback } from "react";
import { motion, AnimatePresence, useScroll, useSpring } from "framer-motion";
import {
  Menu,
  X,
  ArrowRight,
  ChevronDown,
  Layers,
  Award,
  Shield,
  Puzzle,
  Handshake,
  Brain,
  FileText,
  Factory,
  ClipboardCheck,
  Database,
  BarChart3,
  BookOpen,
  ShieldCheck,
  LineChart,
  AlertTriangle,
  Sparkles,
  Building2,
  Lightbulb,
  Bot,
  Sun,
  Moon,
  Globe,
  Briefcase,
} from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTheme } from "../layout/ThemeContext";
import logo from "../../../public/ESGCaffe Light Mode Logo.png";

/* ------------------------------------------------------------------ */
/*  SaaS login URL helper                                              */
/* ------------------------------------------------------------------ */
const getSaasLoginUrl = () => {
  const envUrl = import.meta.env.VITE_SAAS_URL;
  if (envUrl) return `${envUrl}/login`;
  const { protocol, hostname, port } = window.location;
  if (hostname === "localhost" || hostname === "127.0.0.1") {
    return `${protocol}//saas.localhost${port ? `:${port}` : ""}/login`;
  }
  return `${protocol}//saas.esg.datacaffe.ai/login`;
};

/* ------------------------------------------------------------------ */
/*  Navigation data                                                    */
/* ------------------------------------------------------------------ */
const primaryLinks = [
  { label: "Features", path: "/features", dropdownType: "megaMenu" },
  { label: "Frameworks", path: "/frameworks", dropdownType: "frameworks" },
  { label: "Industries", path: "/industries", dropdownType: "industries" },
  { label: "Resources", path: "/blog", dropdownType: "resources" },
  { label: "Pricing", path: "/pricing" },
  { label: "Partnerships", path: "/partnerships" },
  { label: "Announcements", path: "/announcements" },
];

/* ------------------------------------------------------------------ */
/*  Resources dropdown data — 3 columns + featured card               */
/* ------------------------------------------------------------------ */
const resourcesColumns = [
  {
    heading: "Learn",
    icon: Lightbulb,
    items: [
      { label: "AI Platform", desc: "ESG AI Co-Pilot overview", path: "/ai-platform" },
    ],
  },
  {
    heading: "Guides & Docs",
    icon: FileText,
    items: [
      { label: "API Reference", desc: "Build with ESGCaffe API", path: "/api" },
    ],
  },
  {
    heading: "Company",
    icon: Building2,
    items: [
      { label: "About Us", desc: "Our mission & team", path: "/about" },
      { label: "Contact Us", desc: "Talk to our team", path: "/contact" },
    ],
  },
];

/* ------------------------------------------------------------------ */
/*  Mega Menu data — 3 columns                                         */
/* ------------------------------------------------------------------ */
const megaMenuColumns = [
  {
    heading: "Platform",
    items: [
      { label: "Platform Overview", desc: "Complete ESG management suite", icon: Layers, path: "/features" },
      { label: "Why ESGCaffe?", desc: "See how we compare", icon: Award, path: "/comparison" },
      { label: "Security & Trust", desc: "Enterprise-grade data protection", icon: Shield, path: "/security" },
      { label: "Integrations", desc: "Connect your existing tools", icon: Puzzle, path: "/integrations" },
    ],
  },
  {
    heading: "Capabilities",
    items: [
      { label: "ESG AI Co-Pilot", desc: "AI-powered insights & automation", icon: Brain, path: "/ai-platform" },
      { label: "Unified Reporting", desc: "Multi-framework report generation", icon: FileText, path: "/frameworks" },
      { label: "Carbon Accounting", desc: "Scope 1, 2 & 3 tracking", icon: Factory, path: "/features" },
      { label: "Compliance Automation", desc: "Automated gap analysis & alerts", icon: ClipboardCheck, path: "/features" },
      { label: "Data Management", desc: "Centralized ESG data collection", icon: Database, path: "/features" },
      { label: "Analytics Dashboard", desc: "Real-time KPIs & visualizations", icon: BarChart3, path: "/features" },
    ],
  },
  {
    heading: "Solutions",
    items: [
      { label: "ESG Reporting", desc: "GRI, SASB, TCFD & CSRD ready", icon: BookOpen, path: "/frameworks" },
      { label: "Sustainability Assurance", desc: "Audit-ready documentation", icon: ShieldCheck, path: "/security" },
      { label: "Financial + ESG Reporting", desc: "Integrated disclosure workflows", icon: LineChart, path: "/features" },
      { label: "Risk & Compliance", desc: "Enterprise risk management", icon: AlertTriangle, path: "/features" },
    ],
  },
];

/* ------------------------------------------------------------------ */
/*  Frameworks dropdown data                                           */
/* ------------------------------------------------------------------ */
const frameworkColumns = [
  {
    heading: "Global Standards",
    items: [
      { label: "GRI", desc: "Global Reporting Initiative", icon: Globe, path: "/frameworks/gri" },
      { label: "SASB", desc: "Sustainability Accounting Standards", icon: BarChart3, path: "/frameworks/sasb" },
      { label: "ISSB (IFRS S1 & S2)", desc: "International Sustainability Standards Board", icon: LineChart, path: "/frameworks/issb" },
      { label: "TCFD", desc: "Climate Financial Disclosures", icon: AlertTriangle, path: "/frameworks/tcfd" },
      { label: "CDP", desc: "Carbon Disclosure Project", icon: Factory, path: "/frameworks/cdp" },
    ],
  },
  {
    heading: "EU & South Asia",
    items: [
      { label: "CSRD / ESRS", desc: "EU Corporate Sustainability Reporting", icon: FileText, path: "/frameworks/csrd-esrs" },
      { label: "SEBI BRSR", desc: "Business Responsibility & Sustainability", icon: Shield, path: "/frameworks/sebi-brsr" },
    ],
  },
  {
    heading: "Asia-Pacific",
    items: [
      { label: "Bursa Malaysia", desc: "Bursa Malaysia Sustainability Reporting", icon: Building2, path: "/frameworks/bursa-my" },
      { label: "SGX-SR", desc: "SGX Sustainability Reporting", icon: Database, path: "/frameworks/sgx-sr" },
      { label: "Thailand SET/SEC", desc: "Thailand SET/SEC Framework", icon: ClipboardCheck, path: "/frameworks/set-th" },
      { label: "AASB S1", desc: "General Sustainability Requirements", icon: ShieldCheck, path: "/frameworks/aasb-s1" },
      { label: "AASB S2", desc: "Climate-related Financial Disclosures", icon: BookOpen, path: "/frameworks/aasb-s2" },
      { label: "ASRS", desc: "Australian Sustainability Reporting Standards", icon: Layers, path: "/frameworks/asrs" },
    ],
  },
];

// Flat list for mobile accordion
const frameworkItems = frameworkColumns.flatMap((col) => col.items);

/* ------------------------------------------------------------------ */
/*  Industries dropdown data                                           */
/* ------------------------------------------------------------------ */
const industriesDropdown = {
  sections: [
    {
      heading: "By Industry",
      items: [
        {
          label: "Manufacturing",
          desc: "Multi-plant ESG & BRSR compliance",
          icon: Factory,
          path: "/industries/manufacturing",
          comingSoon: false,
        },
        {
          label: "ESG Consulting",
          desc: "Multi-client advisory & BRSR platform",
          icon: Briefcase,
          path: "/industries/consulting",
          comingSoon: false,
        },
        {
          label: "Financial Services",
          desc: "TCFD, CSRD & green finance",
          icon: LineChart,
          path: "/industries/financial-services",
          comingSoon: false,
        },
        {
          label: "Real Estate",
          desc: "GRESB, energy & carbon reporting",
          icon: Building2,
          path: "/industries/real-estate",
          comingSoon: false,
        },
        {
          label: "Healthcare",
          desc: "GRI social & supply chain ESG",
          icon: ShieldCheck,
          path: "/industries/healthcare",
          comingSoon: false,
        },
      ],
    },
  ],
};

/* ------------------------------------------------------------------ */
/*  Animation configs                                                  */
/* ------------------------------------------------------------------ */
const dropdownAnim = {
  initial: { opacity: 0, y: -8, scale: 0.98, pointerEvents: 'none' },
  animate: {
    opacity: 1, y: 0, scale: 1, pointerEvents: 'auto',
    transition: { duration: 0.25, ease: [0.25, 0.46, 0.45, 0.94] },
  },
  exit: {
    opacity: 0, y: -6, scale: 0.98, pointerEvents: 'none',
    transition: { duration: 0.18, ease: [0.25, 0.46, 0.45, 0.94] },
  },
};

const itemStagger = {
  animate: { transition: { staggerChildren: 0.03, delayChildren: 0.08 } },
};

const itemChild = {
  initial: { opacity: 0, x: -6 },
  animate: { opacity: 1, x: 0, transition: { duration: 0.22 } },
};

/* ------------------------------------------------------------------ */
/*  Mega Menu Item                                                     */
/* ------------------------------------------------------------------ */
const MegaMenuItem = ({ item, onClick }) => {
  const Icon = item.icon;
  const location = useLocation();
  const isActive = item.path && location.pathname === item.path;

  const content = (
    <motion.div
      variants={itemChild}
      className={`group/item flex items-start gap-3 px-3 py-2.5 rounded-xl cursor-pointer transition-all duration-200
        ${isActive ? "bg-blue-50 dark:bg-blue-900/30" : "hover:bg-slate-50 dark:hover:bg-slate-700/50"}`}
    >
      <div className={`flex-shrink-0 mt-0.5 w-9 h-9 rounded-lg flex items-center justify-center transition-all duration-200
        ${isActive ? "bg-blue-100 dark:bg-blue-900/40 text-blue-600" : "bg-slate-100 dark:bg-slate-700/50 text-slate-500 dark:text-slate-400 group-hover/item:bg-blue-50 dark:group-hover/item:bg-blue-900/30 group-hover/item:text-blue-600"}`}
      >
        <Icon className="w-[18px] h-[18px]" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5">
          <span className={`text-sm font-semibold transition-colors duration-200 ${isActive ? "text-blue-600" : "text-slate-800 dark:text-slate-200 group-hover/item:text-blue-600"}`}>
            {item.label}
          </span>
          {item.external && (
            <ArrowRight className="w-3 h-3 text-slate-400 -rotate-45 group-hover/item:text-blue-500 transition-colors" />
          )}
        </div>
        <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5 leading-relaxed">{item.desc}</p>
      </div>
    </motion.div>
  );

  if (item.external) {
    return (
      <a href={item.href} target="_blank" rel="noopener noreferrer" onClick={onClick}>
        {content}
      </a>
    );
  }

  return (
    <Link to={item.path} onClick={onClick}>
      {content}
    </Link>
  );
};

/* ------------------------------------------------------------------ */
/*  Mega Menu Column                                                   */
/* ------------------------------------------------------------------ */
const MegaMenuColumn = ({ column, isLast, onItemClick }) => (
  <motion.div
    variants={itemStagger}
    initial="initial"
    animate="animate"
    className={`py-5 px-2 ${!isLast ? "border-r border-slate-100 dark:border-slate-600/50" : ""}`}
  >
    <h4 className="text-[11px] font-bold uppercase tracking-[0.1em] text-slate-400 dark:text-slate-500 px-3 mb-2">
      {column.heading}
    </h4>
    <div className="space-y-0.5">
      {column.items.map((item) => (
        <MegaMenuItem key={item.label} item={item} onClick={onItemClick} />
      ))}
    </div>
  </motion.div>
);

/* ------------------------------------------------------------------ */
/*  Mobile Accordion                                                   */
/* ------------------------------------------------------------------ */
const MobileAccordion = ({ title, children, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border-b border-slate-100 dark:border-slate-700 last:border-0">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between py-3.5 text-sm font-semibold text-slate-800 dark:text-slate-200 bg-transparent border-none cursor-pointer"
      >
        {title}
        <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="overflow-hidden"
          >
            <div className="pb-3 pl-1">{children}</div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/*  Hover-intent dropdown hook                                         */
/* ------------------------------------------------------------------ */
const useDropdown = (openDelay = 60, closeDelay = 120) => {
  const [isOpen, setIsOpen] = useState(false);
  const timer = useRef(null);

  const open = useCallback(() => {
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setIsOpen(true), openDelay);
  }, [openDelay]);

  const close = useCallback(() => {
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setIsOpen(false), closeDelay);
  }, [closeDelay]);

  const forceClose = useCallback(() => {
    clearTimeout(timer.current);
    setIsOpen(false);
  }, []);

  useEffect(() => () => clearTimeout(timer.current), []);

  return { isOpen, open, close, forceClose };
};

/* ------------------------------------------------------------------ */
/*  Navbar Component                                                   */
/* ------------------------------------------------------------------ */
const Navbar = () => {
  const { theme, toggleTheme } = useTheme();
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const megaMenu = useDropdown();
  const megaMenuRef = useRef(null);

  const fwDropdown = useDropdown();
  const fwDropdownRef = useRef(null);

  const industriesMenu = useDropdown();
  const industriesMenuRef = useRef(null);

  const resourcesMenu = useDropdown();
  const resourcesMenuRef = useRef(null);

  const loginUrl = getSaasLoginUrl();
  const location = useLocation();
  const navigate = useNavigate();
  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, { stiffness: 100, damping: 30, restDelta: 0.001 });

  const isHomePage = location.pathname === "/esg-360" || location.pathname === "/";
  // Show solid (dark text, white bg) when scrolled, on sub-pages, or on homepage in light mode
  const showSolid = scrolled || !isHomePage || theme === 'light';

  // Scroll detection
  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const closeAll = useCallback(() => {
    megaMenu.close();
    fwDropdown.close();
    industriesMenu.close();
    resourcesMenu.close();
  }, [megaMenu.close, fwDropdown.close, industriesMenu.close, resourcesMenu.close]);

  // Close dropdowns on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      const inMega = megaMenuRef.current?.contains(e.target);
      const inFw = fwDropdownRef.current?.contains(e.target);
      const inInd = industriesMenuRef.current?.contains(e.target);
      const inRes = resourcesMenuRef.current?.contains(e.target);
      if (!inMega && !inFw && !inInd && !inRes) closeAll();
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [closeAll]);

  // Close all on route change
  useEffect(() => {
    closeAll();
    setMobileMenuOpen(false);
  }, [location.pathname]);

  const closeAllAndNavigate = useCallback((path) => {
    closeAll();
    setMobileMenuOpen(false);
    navigate(path);
  }, [closeAll, navigate]);

  const handleNavClick = (link) => closeAllAndNavigate(link.path);

  const isLinkActive = (link) => location.pathname === link.path;

  /* ---- Render a nav button with dropdown chevron ---- */
  const renderDropdownTrigger = (link, dropdown, isOpenState) => {
    const active = isLinkActive(link);
    return (
      <button
        onClick={() => handleNavClick(link)}
        aria-expanded={isOpenState}
        aria-haspopup="true"
        className={`relative flex items-center gap-1 px-2.5 2xl:px-4 py-2 text-sm font-medium whitespace-nowrap transition-all duration-300 bg-transparent border-none cursor-pointer ${
          showSolid
            ? isOpenState || active ? "text-blue-600" : "text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white"
            : isOpenState || active ? "text-white" : "text-white/70 hover:text-white"
        }`}
      >
        {link.label}
        <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${isOpenState ? "rotate-180" : ""}`} />
        <motion.span
          className={`absolute bottom-0 left-1/2 -translate-x-1/2 h-[2px] rounded-full ${showSolid ? "bg-blue-600" : "bg-white"}`}
          initial={false}
          animate={{ width: active || isOpenState ? "60%" : "0%" }}
          transition={{ duration: 0.3 }}
        />
      </button>
    );
  };

  return (
    <>
      <motion.nav
        className="fixed top-3 md:top-5 left-0 right-0 z-50 flex justify-center px-3 md:px-6 pointer-events-none"
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      >
        <div
          className={`pointer-events-auto w-full max-w-[1400px] rounded-full border transition-all duration-300 relative overflow-visible ${
            showSolid
              ? "bg-white/85 dark:bg-[#0f172a]/85 border-slate-200 dark:border-slate-700 shadow-[0_10px_40px_-15px_rgba(15,23,42,0.18)]"
              : "bg-white/55 dark:bg-[#0f172a]/50 border-white/60 dark:border-slate-700/50 shadow-[0_4px_20px_-8px_rgba(15,23,42,0.10)]"
          }`}
          style={{
            backdropFilter: 'saturate(180%) blur(18px)',
            WebkitBackdropFilter: 'saturate(180%) blur(18px)',
          }}
        >
          {/* Scroll progress bar — thin line hugging the pill's bottom edge */}
          <motion.div
            className="absolute bottom-0 left-4 right-4 h-[2px] bg-gradient-to-r from-blue-500 to-emerald-500 origin-left rounded-full"
            style={{ scaleX, opacity: showSolid ? 1 : 0 }}
          />

          <div className="flex items-center justify-between gap-3 xl:gap-5 h-[76px] lg:h-[84px] pl-4 pr-2 md:pl-5 md:pr-3 lg:pl-6 lg:pr-3">
            {/* Logo */}
            <Link to="/esg-360" className="flex items-center gap-2 shrink-0">
              <img
                src="/logo.png"
                alt="DataCaffe"
                className="h-16 w-auto object-contain"
              />
              <div className="flex flex-col leading-tight">
                <span className="text-2xl font-bold tracking-tight">
                  <span style={{ color: '#f97316' }}>ESG</span><span className="text-blue-600">Caffe</span>
                </span>
                <span className="text-[8px] font-medium tracking-[0.28em] text-gray-500 uppercase">GREEN INTELLIGENCE</span>
              </div>
            </Link>

            {/* ============ Desktop Navigation ============ */}
            <div className="hidden lg:flex items-center gap-0.5 min-w-0">
              {primaryLinks.map((link) => {
                const active = isLinkActive(link);

                /* ---------- Features mega menu ---------- */
                if (link.dropdownType === "megaMenu") {
                  return (
                    <div
                      key={link.path}
                      className="relative"
                      ref={megaMenuRef}
                      onMouseEnter={() => { megaMenu.open(); fwDropdown.forceClose(); industriesMenu.forceClose(); resourcesMenu.forceClose(); }}
                      onMouseLeave={megaMenu.close}
                    >
                      {renderDropdownTrigger(link, megaMenu, megaMenu.isOpen)}

                      <AnimatePresence>
                        {megaMenu.isOpen && (
                          <motion.div
                            {...dropdownAnim}
                            className="absolute -left-40 top-full pt-2"
                            onMouseEnter={megaMenu.open}
                            onMouseLeave={megaMenu.close}
                          >
                            <div className="absolute -top-2 left-0 right-0 h-3" />
                            <div
                              className="bg-white dark:bg-[#1e293b] rounded-2xl shadow-[0_20px_60px_rgba(0,0,0,0.12),0_1px_3px_rgba(0,0,0,0.08)] dark:shadow-[0_20px_60px_rgba(0,0,0,0.4)] border border-slate-200/70 dark:border-slate-600/50 overflow-hidden"
                              style={{ width: "820px" }}
                            >
                              <div className="h-[3px] bg-blue-600" />
                              <div className="grid grid-cols-3">
                                {megaMenuColumns.map((col, i) => (
                                  <MegaMenuColumn key={col.heading} column={col} isLast={i === megaMenuColumns.length - 1} onItemClick={megaMenu.close} />
                                ))}
                              </div>
                              <div className="bg-slate-50 dark:bg-slate-800/50 border-t border-slate-100 dark:border-slate-600/50 px-6 py-3.5 flex items-center justify-between">
                                <div className="flex items-center gap-2 text-sm text-slate-500">
                                  <Sparkles className="w-4 h-4 text-blue-500" />
                                  <span>Trusted by <span className="font-semibold text-slate-700">15+ enterprises</span> worldwide</span>
                                </div>
                                <Link to="/features" onClick={megaMenu.close} className="flex items-center gap-1.5 text-sm font-semibold text-blue-600 hover:text-blue-700 transition-colors group/cta">
                                  Explore all features
                                  <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover/cta:translate-x-0.5" />
                                </Link>
                              </div>
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  );
                }

                /* ---------- Frameworks dropdown ---------- */
                if (link.dropdownType === "frameworks") {
                  return (
                    <div
                      key={link.path}
                      className="relative"
                      ref={fwDropdownRef}
                      onMouseEnter={() => { fwDropdown.open(); megaMenu.forceClose(); industriesMenu.forceClose(); resourcesMenu.forceClose(); }}
                      onMouseLeave={fwDropdown.close}
                    >
                      {renderDropdownTrigger(link, fwDropdown, fwDropdown.isOpen)}

                      <AnimatePresence>
                        {fwDropdown.isOpen && (
                          <motion.div
                            {...dropdownAnim}
                            className="absolute -left-40 top-full pt-2"
                            onMouseEnter={fwDropdown.open}
                            onMouseLeave={fwDropdown.close}
                          >
                            <div className="absolute -top-2 left-0 right-0 h-3" />
                            <div
                              className="bg-white dark:bg-[#1e293b] rounded-2xl shadow-[0_20px_60px_rgba(0,0,0,0.12),0_1px_3px_rgba(0,0,0,0.08)] dark:shadow-[0_20px_60px_rgba(0,0,0,0.4)] border border-slate-200/70 dark:border-slate-600/50 overflow-hidden"
                              style={{ width: "820px" }}
                            >
                              <div className="h-[3px] bg-blue-600" />
                              <div className="grid grid-cols-3">
                                {frameworkColumns.map((col, i) => (
                                  <MegaMenuColumn key={col.heading} column={col} isLast={i === frameworkColumns.length - 1} onItemClick={fwDropdown.close} />
                                ))}
                              </div>
                              <div className="bg-slate-50 dark:bg-slate-800/50 border-t border-slate-100 dark:border-slate-600/50 px-6 py-3.5 flex items-center justify-between">
                                <div className="flex items-center gap-2 text-sm text-slate-500">
                                  <Sparkles className="w-4 h-4 text-blue-500" />
                                  <span>Automatic <span className="font-semibold text-slate-700 dark:text-slate-300">cross-framework</span> mapping</span>
                                </div>
                                <button
                                  onClick={() => { fwDropdown.forceClose(); navigate('/frameworks'); }}
                                  className="flex items-center gap-1.5 text-sm font-semibold text-blue-600 hover:text-blue-700 transition-colors group/cta bg-transparent border-none cursor-pointer"
                                >
                                  View all frameworks
                                  <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover/cta:translate-x-0.5" />
                                </button>
                              </div>
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  );
                }

                /* ---------- Industries dropdown ---------- */
                if (link.dropdownType === "industries") {
                  return (
                    <div
                      key={link.label}
                      className="relative"
                      ref={industriesMenuRef}
                      onMouseEnter={() => { industriesMenu.open(); megaMenu.forceClose(); fwDropdown.forceClose(); resourcesMenu.forceClose(); }}
                      onMouseLeave={industriesMenu.close}
                    >
                      {renderDropdownTrigger(link, industriesMenu, industriesMenu.isOpen)}

                      <AnimatePresence>
                        {industriesMenu.isOpen && (
                          <motion.div
                            {...dropdownAnim}
                            className="absolute -left-20 top-full pt-2"
                            onMouseEnter={industriesMenu.open}
                            onMouseLeave={industriesMenu.close}
                          >
                            <div className="absolute -top-2 left-0 right-0 h-3" />
                            <div
                              className="bg-white dark:bg-[#1e293b] rounded-2xl shadow-[0_20px_60px_rgba(0,0,0,0.12),0_1px_3px_rgba(0,0,0,0.08)] dark:shadow-[0_20px_60px_rgba(0,0,0,0.4)] border border-slate-200/70 dark:border-slate-600/50 overflow-hidden"
                              style={{ width: "420px" }}
                            >
                              <div className="h-[3px] bg-gradient-to-r from-emerald-500 to-teal-500" />
                              <div className="py-4 px-4">
                                {industriesDropdown.sections.map((section, sIdx) => {
                                  return (
                                    <div key={section.heading} className={sIdx > 0 ? "mt-3 pt-3 border-t border-slate-100 dark:border-slate-600/50" : ""}>
                                      <h4 className="text-[11px] font-bold uppercase tracking-[0.1em] text-slate-400 dark:text-slate-500 px-2 mb-2">
                                        {section.heading}
                                      </h4>
                                      <motion.div variants={itemStagger} initial="initial" animate="animate" className="space-y-0.5">
                                        {section.items.map((item) => {
                                          const Icon = item.icon;
                                          if (item.comingSoon) {
                                            return (
                                              <motion.div
                                                key={item.label}
                                                variants={itemChild}
                                                className="flex items-start gap-3 px-3 py-2.5 rounded-xl opacity-50 cursor-not-allowed"
                                              >
                                                <div className="flex-shrink-0 mt-0.5 w-9 h-9 rounded-lg bg-slate-100 dark:bg-slate-700/50 text-slate-400 flex items-center justify-center">
                                                  <Icon className="w-[18px] h-[18px]" />
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                  <div className="flex items-center gap-2">
                                                    <span className="text-sm font-semibold text-slate-800 dark:text-slate-200">{item.label}</span>
                                                    <span className="text-[9px] font-bold uppercase tracking-wider text-slate-400 bg-slate-100 dark:bg-slate-700 px-1.5 py-0.5 rounded-full">Soon</span>
                                                  </div>
                                                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5 leading-relaxed">{item.desc}</p>
                                                </div>
                                              </motion.div>
                                            );
                                          }
                                          return (
                                            <Link key={item.label} to={item.path} onClick={industriesMenu.forceClose}>
                                              <motion.div
                                                variants={itemChild}
                                                className={`group/item flex items-start gap-3 px-3 py-2.5 rounded-xl cursor-pointer transition-all duration-200 ${
                                                  location.pathname === item.path
                                                    ? "bg-emerald-50 dark:bg-emerald-900/30"
                                                    : "hover:bg-slate-50 dark:hover:bg-slate-700/50"
                                                }`}
                                              >
                                                <div className={`flex-shrink-0 mt-0.5 w-9 h-9 rounded-lg flex items-center justify-center transition-all duration-200 ${
                                                  location.pathname === item.path
                                                    ? "bg-emerald-100 dark:bg-emerald-900/40 text-emerald-600"
                                                    : "bg-slate-100 dark:bg-slate-700/50 text-slate-500 dark:text-slate-400 group-hover/item:bg-emerald-50 dark:group-hover/item:bg-emerald-900/30 group-hover/item:text-emerald-600"
                                                }`}>
                                                  <Icon className="w-[18px] h-[18px]" />
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                  <span className={`text-sm font-semibold transition-colors duration-200 block ${
                                                    location.pathname === item.path
                                                      ? "text-emerald-600"
                                                      : "text-slate-800 dark:text-slate-200 group-hover/item:text-emerald-600"
                                                  }`}>
                                                    {item.label}
                                                  </span>
                                                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5 leading-relaxed">{item.desc}</p>
                                                </div>
                                              </motion.div>
                                            </Link>
                                          );
                                        })}
                                      </motion.div>
                                    </div>
                                  );
                                })}
                              </div>
                              <div className="bg-slate-50 dark:bg-slate-800/50 border-t border-slate-100 dark:border-slate-600/50 px-6 py-3.5 flex items-center justify-between">
                                <div className="flex items-center gap-2 text-sm text-slate-500">
                                  <Factory className="w-4 h-4 text-emerald-500" />
                                  <span>Industry-specific ESG intelligence</span>
                                </div>
                              </div>
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  );
                }

                /* ---------- Resources mega menu ---------- */
                if (link.dropdownType === "resources") {
                  return (
                    <div
                      key={link.label}
                      className="relative"
                      ref={resourcesMenuRef}
                      onMouseEnter={() => { resourcesMenu.open(); megaMenu.forceClose(); fwDropdown.forceClose(); industriesMenu.forceClose(); }}
                      onMouseLeave={resourcesMenu.close}
                    >
                      {renderDropdownTrigger(link, resourcesMenu, resourcesMenu.isOpen)}

                      <AnimatePresence>
                        {resourcesMenu.isOpen && (
                          <motion.div
                            {...dropdownAnim}
                            className="absolute -left-60 top-full pt-2"
                            onMouseEnter={resourcesMenu.open}
                            onMouseLeave={resourcesMenu.close}
                          >
                            <div className="absolute -top-2 left-0 right-0 h-3" />
                            <div
                              className="bg-white dark:bg-[#1e293b] rounded-2xl shadow-[0_20px_60px_rgba(0,0,0,0.12),0_1px_3px_rgba(0,0,0,0.08)] dark:shadow-[0_20px_60px_rgba(0,0,0,0.4)] border border-slate-200/70 dark:border-slate-600/50 overflow-hidden"
                              style={{ width: "860px" }}
                            >
                              <div className="h-[3px] bg-blue-600" />
                              <div className="flex">
                                {/* 3 text columns */}
                                <div className="flex flex-1 divide-x divide-slate-100 dark:divide-slate-600/50 px-4 py-5">
                                  {resourcesColumns.map((col) => {
                                    const ColIcon = col.icon;
                                    return (
                                      <div key={col.heading} className="flex-1 px-4">
                                        <div className="flex items-center gap-2 mb-3">
                                          <ColIcon className="w-4 h-4 text-blue-600" />
                                          <h4 className="text-[11px] font-bold uppercase tracking-widest text-slate-700 dark:text-slate-300">
                                            {col.heading}
                                          </h4>
                                        </div>
                                        <ul className="space-y-0.5">
                                          {col.items.map((item) => (
                                            <li key={item.label}>
                                              <Link
                                                to={item.path}
                                                onClick={() => { resourcesMenu.forceClose(); closeAll(); }}
                                                className={`w-full text-left block px-2 py-1.5 rounded-md transition-colors ${
                                                  location.pathname === item.path
                                                    ? "bg-blue-50 dark:bg-blue-900/30 text-blue-600"
                                                    : "text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700/50 hover:text-blue-600"
                                                }`}
                                              >
                                                <span className="text-sm font-semibold block">{item.label}</span>
                                                <span className="text-[11px] text-slate-400 leading-tight mt-0.5">{item.desc}</span>
                                              </Link>
                                            </li>
                                          ))}
                                        </ul>
                                      </div>
                                    );
                                  })}
                                </div>

                                {/* Featured card */}
                                <div className="w-[200px] flex-shrink-0 bg-slate-50 dark:bg-slate-800/50 border-l border-slate-100 dark:border-slate-600/50 p-5 flex flex-col">
                                  <div className="w-10 h-10 rounded-xl bg-blue-600 flex items-center justify-center mb-3 shadow-md shadow-blue-500/20">
                                    <Bot className="w-5 h-5 text-white" />
                                  </div>
                                  <h4 className="text-sm font-bold text-slate-800 dark:text-slate-200 leading-snug">
                                    ESG AI Co-Pilot
                                  </h4>
                                  <p className="mt-1.5 text-[11px] text-slate-500 dark:text-slate-400 leading-relaxed flex-1">
                                    Get real-time answers to your ESG compliance questions with our AI assistant.
                                  </p>
                                  <Link
                                    to="/ai-platform"
                                    onClick={() => { resourcesMenu.forceClose(); closeAll(); }}
                                    className="mt-4 flex items-center gap-1 text-sm font-semibold text-blue-600 hover:text-blue-700 transition-colors group/feat"
                                  >
                                    Try AI Co-Pilot
                                    <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover/feat:translate-x-0.5" />
                                  </Link>
                                </div>
                              </div>

                              {/* Bottom bar */}
                              <div className="bg-slate-50 dark:bg-slate-800/50 border-t border-slate-100 dark:border-slate-600/50 px-5 py-3 flex items-center justify-between">
                                <span className="text-xs text-slate-500">Everything you need to get started with ESG compliance</span>
                                <Link
                                  to="/ai-platform"
                                  onClick={() => { resourcesMenu.forceClose(); closeAll(); }}
                                  className="flex items-center gap-1.5 text-sm font-semibold text-blue-600 hover:text-blue-700 transition-colors group/cta"
                                >
                                  See all resources
                                  <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover/cta:translate-x-0.5" />
                                </Link>
                              </div>
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  );
                }

                /* ---------- Regular nav links ---------- */
                return (
                  <button
                    key={link.path}
                    onClick={() => handleNavClick(link)}
                    className={`relative px-2.5 2xl:px-4 py-2 text-sm font-medium whitespace-nowrap transition-all duration-300 bg-transparent border-none cursor-pointer ${
                      showSolid
                        ? active ? "text-blue-600" : "text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white"
                        : active ? "text-white" : "text-white/70 hover:text-white"
                    }`}
                  >
                    {link.label}
                    <motion.span
                      className={`absolute bottom-0 left-1/2 -translate-x-1/2 h-[2px] rounded-full ${showSolid ? "bg-blue-600" : "bg-white"}`}
                      initial={false}
                      animate={{ width: active ? "60%" : "0%" }}
                      transition={{ duration: 0.3 }}
                    />
                  </button>
                );
              })}
            </div>

            {/* ============ Right Side Actions ============ */}
            <div className="hidden lg:flex items-center gap-2 xl:gap-3 shrink-0">
              <a
                href={loginUrl}
                className={`px-3 xl:px-4 py-2 text-sm font-medium rounded-lg whitespace-nowrap transition-all duration-300 ${
                  showSolid
                    ? "text-slate-700 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-700"
                    : "text-white/80 hover:text-white"
                }`}
              >
                Sign In
              </a>
              <motion.button
                onClick={() => window.open("https://meetings-na2.hubspot.com/datacaffe-ai/esgcaffe-discovery-call", "_blank")}
                className={`flex items-center gap-2 px-4 xl:px-5 py-2.5 text-sm font-semibold rounded-md whitespace-nowrap transition-all duration-300 ${
                  showSolid
                    ? "bg-blue-500 text-white shadow-sm hover:bg-blue-600"
                    : "bg-white/15 backdrop-blur-md text-white border border-white/20 hover:bg-white/25"
                }`}
              >
                Book a Demo
                <ArrowRight className="w-4 h-4" />
              </motion.button>
            </div>

            {/* ============ Mobile Toggle ============ */}
            <div className="lg:hidden flex items-center gap-2">
              <button
                className={`p-2 rounded-lg transition-colors ${
                  showSolid ? "text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700" : "text-slate-800 dark:text-white hover:bg-slate-100/50 dark:hover:bg-white/10"
                }`}
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                aria-label="Toggle menu"
              >
                {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </div>
      </motion.nav>

      {/* ============ Mobile Menu ============ */}
      <AnimatePresence>
          {mobileMenuOpen && (
            <>
              {/* Backdrop overlay */}
              <motion.div
                className="lg:hidden fixed inset-0 top-[72px] bg-black/40 z-40"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={() => setMobileMenuOpen(false)}
              />
              <motion.div
                className="lg:hidden fixed top-[72px] left-0 right-0 bottom-0 z-50 bg-white dark:bg-[#0f172a] border-t border-slate-100 dark:border-slate-700 overflow-y-auto"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3 }}
              >
              <div className="px-6 py-4">
                {/* Features accordion */}
                <MobileAccordion title="Features" defaultOpen>
                  {megaMenuColumns.map((col) => (
                    <div key={col.heading} className="mb-3 last:mb-0">
                      <p className="text-[10px] font-bold uppercase tracking-[0.1em] text-slate-400 mb-1.5 pl-1">
                        {col.heading}
                      </p>
                      {col.items.map((item) => {
                        const Icon = item.icon;
                        if (item.external) {
                          return (
                            <a key={item.label} href={item.href} target="_blank" rel="noopener noreferrer" onClick={() => setMobileMenuOpen(false)} className="flex items-center gap-2.5 py-2 pl-1 text-sm text-slate-700 hover:text-blue-600 transition-colors">
                              <Icon className="w-4 h-4 text-slate-400" />
                              {item.label}
                              <ArrowRight className="w-3 h-3 text-slate-300 -rotate-45 ml-auto" />
                            </a>
                          );
                        }
                        return (
                          <Link key={item.label} to={item.path} onClick={() => setMobileMenuOpen(false)} className={`flex items-center gap-2.5 py-2 pl-1 text-sm transition-colors ${location.pathname === item.path ? "text-blue-600 font-medium" : "text-slate-700 hover:text-blue-600"}`}>
                            <Icon className="w-4 h-4 text-slate-400" />
                            {item.label}
                          </Link>
                        );
                      })}
                    </div>
                  ))}
                </MobileAccordion>

                {/* Frameworks accordion */}
                <MobileAccordion title="Frameworks">
                  {frameworkItems.map((fw) => (
                    <Link
                      key={fw.label}
                      to={fw.path}
                      onClick={() => setMobileMenuOpen(false)}
                      className="flex items-center gap-2.5 py-2 pl-1 text-sm text-slate-700 hover:text-blue-600 transition-colors"
                    >
                      <div className="w-2 h-2 rounded-full bg-blue-400 flex-shrink-0" />
                      <span className="font-medium">{fw.label}</span>
                      <span className="text-slate-400 text-xs">— {fw.desc}</span>
                    </Link>
                  ))}
                  <Link
                    to="/frameworks"
                    onClick={() => setMobileMenuOpen(false)}
                    className="flex items-center gap-1.5 mt-2 pl-1 text-sm font-semibold text-blue-600"
                  >
                    View all frameworks
                    <ArrowRight className="w-3.5 h-3.5" />
                  </Link>
                </MobileAccordion>

                {/* Industries accordion */}
                <MobileAccordion title="Industries">
                  {industriesDropdown.sections.map((section) => (
                    <div key={section.heading} className="mb-3 last:mb-0">
                      <p className="text-[10px] font-bold uppercase tracking-[0.1em] text-slate-400 mb-1.5 pl-1">
                        {section.heading}
                      </p>
                      {section.items.map((item) => {
                        const Icon = item.icon;
                        if (item.comingSoon) {
                          return (
                            <div
                              key={item.label}
                              className="flex items-center gap-2.5 py-2 pl-1 opacity-50 cursor-not-allowed"
                            >
                              <Icon className="w-4 h-4 text-slate-400" />
                              <span className="text-sm text-slate-700 dark:text-slate-400">{item.label}</span>
                              <span className="text-[9px] font-bold uppercase tracking-wide text-slate-400 bg-slate-100 dark:bg-slate-700 px-1.5 py-0.5 rounded-full ml-auto">
                                Soon
                              </span>
                            </div>
                          );
                        }
                        return (
                          <Link
                            key={item.label}
                            to={item.path}
                            onClick={() => setMobileMenuOpen(false)}
                            className={`flex items-center gap-2.5 py-2 pl-1 text-sm transition-colors ${
                              location.pathname === item.path
                                ? "text-emerald-600 font-medium"
                                : "text-slate-700 hover:text-emerald-600 dark:text-slate-300 dark:hover:text-emerald-400"
                            }`}
                          >
                            <Icon className="w-4 h-4 text-slate-400" />
                            {item.label}
                          </Link>
                        );
                      })}
                    </div>
                  ))}
                </MobileAccordion>

                {/* Resources accordion */}
                <MobileAccordion title="Resources">
                  {resourcesColumns.map((col) => {
                    const ColIcon = col.icon;
                    return (
                      <div key={col.heading} className="mb-3 last:mb-0">
                        <div className="flex items-center gap-1.5 mb-1.5 pl-1">
                          <ColIcon className="w-3.5 h-3.5 text-blue-500" />
                          <p className="text-[10px] font-bold uppercase tracking-[0.1em] text-slate-400">{col.heading}</p>
                        </div>
                        {col.items.map((item) => (
                          <Link
                            key={item.label}
                            to={item.path}
                            onClick={() => setMobileMenuOpen(false)}
                            className={`block py-2 pl-1 text-sm transition-colors ${location.pathname === item.path ? "text-blue-600 font-medium" : "text-slate-700 hover:text-blue-600"}`}
                          >
                            {item.label}
                          </Link>
                        ))}
                      </div>
                    );
                  })}
                  <Link
                    to="/ai-platform"
                    onClick={() => setMobileMenuOpen(false)}
                    className="flex items-center gap-1.5 mt-2 pl-1 text-sm font-semibold text-blue-600"
                  >
                    Try ESG AI Co-Pilot
                    <ArrowRight className="w-3.5 h-3.5" />
                  </Link>
                </MobileAccordion>

                {/* Other primary links (Pricing) */}
                {primaryLinks
                  .filter((l) => !l.dropdownType)
                  .map((link) => (
                    <button
                      key={link.path}
                      className={`block w-full text-left py-3.5 text-sm font-semibold bg-transparent border-none cursor-pointer transition-colors border-b border-slate-100 ${
                        isLinkActive(link) ? "text-blue-600" : "text-slate-800 hover:text-blue-600"
                      }`}
                      onClick={() => handleNavClick(link)}
                    >
                      {link.label}
                    </button>
                  ))}

                {/* CTA buttons */}
                <div className="pt-5 mt-2 border-t border-slate-100 dark:border-slate-700 space-y-3">
                  <a href={loginUrl} className="block w-full text-center py-3 text-sm font-medium text-slate-700 dark:text-slate-300 border border-slate-200 dark:border-slate-600 rounded-md hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors">
                    Sign In
                  </a>
                  <button
                    onClick={() => window.open("https://meetings-na2.hubspot.com/datacaffe-ai/esgcaffe-discovery-call", "_blank")}
                    className="block w-full text-center py-3 bg-blue-500 hover:bg-blue-600 text-white text-sm font-semibold rounded-md shadow-sm transition-colors"
                  >
                    Book a Demo
                  </button>
                </div>
              </div>
            </motion.div>
            </>
          )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
